import { pluralRulesFor } from "./format"
import { DEFAULT_LANGUAGE, type Language } from "./languages"

type PluralLeaf = { other: string } & Partial<
  Record<Intl.LDMLPluralRule, string>
>
type Leaf = string | PluralLeaf
type Table = Record<string, unknown>
export type NamespaceTable = Record<Language, Table>
type Params = Record<string, string | number>

const isPluralLeaf = (v: unknown): v is PluralLeaf =>
  typeof v === "object" && v !== null && "other" in v

const interpolate = (template: string, params?: Params): string => {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (m, name: string) =>
    name in params ? String(params[name]) : m
  )
}

const resolvePlural = (
  leaf: PluralLeaf,
  language: Language,
  params?: Params
): string => {
  const count = Number(params?.count ?? 0)
  const category = pluralRulesFor(language).select(count)
  return leaf[category] ?? leaf.other
}

const readPath = (root: unknown, key: string): Leaf | undefined => {
  let cursor: unknown = root
  for (const seg of key.split(".")) {
    if (cursor === null || typeof cursor !== "object") return undefined
    cursor = (cursor as Record<string, unknown>)[seg]
  }
  return cursor as Leaf | undefined
}

type LeafKeyPaths<T> = {
  [K in keyof T & string]: T[K] extends Leaf
    ? K
    : T[K] extends object
      ? `${K}.${LeafKeyPaths<T[K]>}`
      : never
}[keyof T & string]

type MergedKey<NS extends Record<string, NamespaceTable>> = {
  [N in keyof NS & string]: `${N}.${LeafKeyPaths<NS[N]["en"]>}`
}[keyof NS & string]

/**
 * Build a translator bound to `language`, scoped to the passed `namespaces`.
 * The caller imports only the namespace modules it uses, so the bundler
 * tree-shakes every namespace not passed here. Keys are dot-paths
 * `"<namespace>.<key>"`. Per-key runtime fallback to the English leaf of the
 * same namespace (defensive; the type already enforces parity).
 */
export const getT = <NS extends Record<string, NamespaceTable>>(
  language: Language,
  namespaces: NS
) => {
  const primaryRoot: Record<string, unknown> = {}
  const enRoot: Record<string, unknown> = {}
  for (const ns of Object.keys(namespaces)) {
    const table = namespaces[ns]
    primaryRoot[ns] = table[language] ?? table[DEFAULT_LANGUAGE]
    enRoot[ns] = table.en
  }
  return (key: MergedKey<NS>, params?: Params): string => {
    const leaf = readPath(primaryRoot, key) ?? readPath(enRoot, key)
    if (leaf === undefined) return key
    if (isPluralLeaf(leaf))
      return interpolate(resolvePlural(leaf, language, params), params)
    return interpolate(leaf, params)
  }
}

export type TFunction = ReturnType<typeof getT>
